import { Link } from "react-router-dom";
import { ArrowRight, Crown, HandHeart, Sparkle } from "@phosphor-icons/react";

const FOUNDER_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_slay-collective/artifacts/6edljsd9_Founder-%20La%27Daena%20Londo.png";

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-28 pb-24" data-testid="about-page">
      {/* Hero */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto mb-24 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4 font-medium animate-fade-in">Our Story</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light text-[#FDFBF7] tracking-tighter max-w-3xl mx-auto leading-tight">
          The Art of Leading Softly
        </h1>
        <p className="text-[#A1A1AA] text-base font-light max-w-xl mx-auto mt-6 leading-relaxed">
          Luxury leadership. Soft power. Unforgettable presence.
        </p>
      </section>

      {/* Founder story */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto mb-24" data-testid="founder-story-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative bg-[#0f0f0f] border border-[rgba(212,175,55,0.15)] overflow-hidden">
            <img
              src={FOUNDER_IMG}
              alt="La'Daena Londo, Founder of Lead.Serve.Slay"
              className="w-full h-auto object-contain"
              data-testid="founder-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2 font-medium">Founder & Visionary</p>
              <p className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] tracking-tight">La'Daena Londo</p>
            </div>
          </div>

          <div className="space-y-8">
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#FDFBF7] tracking-tight">
              Born from a Simple Conviction
            </h2>
            <div className="space-y-6 text-[#A1A1AA] text-sm md:text-base font-light leading-relaxed">
              <p>
                True power doesn't shout. It lingers — like a fine fragrance that enters the room
                a moment before you do and stays long after you've gone.
              </p>
              <p>
                Lead•Serve•Slay™ was founded by La'Daena Londo for the woman who understands that
                elegance is a strategy, softness is a strength, and presence is a language all its own.
                What began as a personal ritual — a signature scent, a moment of care, an armor of confidence —
                became a collection crafted to help every woman claim the room she walks into.
              </p>
              <p>
                Each fragrance oil, body butter, and piece in our collection is created with intention:
                to remind you that you were never meant to blend in. You were made to lead, called to serve,
                and destined to slay.
              </p>
            </div>
            <p className="font-['Cormorant_Garamond'] text-xl text-[#D4AF37] font-light italic">
              — La'Daena Londo
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto mb-24" data-testid="brand-pillars-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-10 hover:border-[#D4AF37]/30 transition-colors duration-500">
            <Crown size={28} weight="thin" className="text-[#D4AF37] mb-6" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7] mb-4 tracking-tight">Lead</h3>
            <p className="text-[#A1A1AA] text-sm font-light leading-relaxed">
              Walk into every room as if it were built for you — because it was. Leadership is not a title;
              it is the quiet certainty you carry.
            </p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-10 hover:border-[#D4AF37]/30 transition-colors duration-500">
            <HandHeart size={28} weight="thin" className="text-[#D4AF37] mb-6" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7] mb-4 tracking-tight">Serve</h3>
            <p className="text-[#A1A1AA] text-sm font-light leading-relaxed">
              Excellence is an act of service. We pour intention into every drop and every stitch,
              so you can pour into everything — and everyone — you love.
            </p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-10 hover:border-[#D4AF37]/30 transition-colors duration-500">
            <Sparkle size={28} weight="thin" className="text-[#D4AF37] mb-6" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7] mb-4 tracking-tight">Slay</h3>
            <p className="text-[#A1A1AA] text-sm font-light leading-relaxed">
              Presence is the ultimate luxury. When you feel extraordinary, you become unforgettable —
              and unforgettable women change the world.
            </p>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="px-6 lg:px-12 max-w-4xl mx-auto mb-24 text-center">
        <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#FDFBF7] tracking-tight leading-snug">
          "Wealth. Power. Confidence. <span className="text-[#D4AF37]">Not worn — embodied.</span>"
        </p>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto text-center">
        <Link
          to="/shop"
          className="btn-luxury inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500"
          data-testid="about-shop-cta"
        >
          Explore the Collection <ArrowRight size={16} weight="bold" />
        </Link>
      </section>
    </div>
  );
}
